import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getUsers,
  getUser,
  createUser,
  updateUser,
  getUserProducts,
  getUserEntitlements,
  type CreateUserRequest,
  type UpdateUserRequest,
} from '../api/users';

export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: getUsers,
  });
}

export function useUser(urn: string) {
  return useQuery({
    queryKey: ['users', urn],
    queryFn: () => getUser(urn),
    enabled: !!urn,
  });
}

export function useUserProducts(urn: string) {
  return useQuery({
    queryKey: ['users', urn, 'products'],
    queryFn: () => getUserProducts(urn),
    enabled: !!urn,
  });
}

export function useUserEntitlements(urn: string) {
  return useQuery({
    queryKey: ['users', urn, 'entitlements'],
    queryFn: () => getUserEntitlements(urn),
    enabled: !!urn,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateUserRequest) => createUser(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ urn, data }: { urn: string; data: UpdateUserRequest }) =>
      updateUser(urn, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}
